import { useMemo } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import EmptyState from "@/components/EmptyState";
import CareerGoalsTab from "./_CareerGoalsTab";
import CareerGoalsTabSkeleton from "./_CareerGoalsTabSkeleton";
import CareerProfileFocusedChrome from "./_CareerProfileFocusedChrome";
import { useCareerProfileQuery } from "./_useCareerProfileQuery";
import { buildCareerPlanHistoryRows, goalsAsOfDay, LATEST_DAY_KEY } from "./careerPlanHistory";
import { careerProfileExperienceIndexHref, careerProfilePathFromLatest } from "./careerPlanLatest";

export default function CareerPlanDayPage() {
  const { day } = useParams();
  const { pathname, search } = useLocation();
  const { pgaId, flagsLoaded, capabilitiesLoaded, employmentRedirect, loading, error, careerIntents, assessment } =
    useCareerProfileQuery();
  const dayKey = day || LATEST_DAY_KEY;
  const historyRows = useMemo(
    () =>
      buildCareerPlanHistoryRows({
        goals: careerIntents,
        assessment,
        includeExperienceIndexHistory: false
      }),
    [assessment, careerIntents]
  );
  const row = historyRows.find((item) => item.dayKey === dayKey);
  const goals = useMemo(() => goalsAsOfDay(careerIntents, dayKey), [careerIntents, dayKey]);

  if (!flagsLoaded || !capabilitiesLoaded) return null;
  if (employmentRedirect) return <Navigate to={employmentRedirect} replace />;
  if (!pgaId) return null;
  // Generouted nests [day]/assessment under this route. Yield the outlet so
  // the nested redirect can keep ?competencyId= instead of sending Results.
  if (/\/assessment\/?$/.test(pathname)) {
    return <Outlet />;
  }
  if (dayKey === "experience-index") {
    return <Navigate to={`${careerProfileExperienceIndexHref(pgaId)}${search}`} replace />;
  }
  if (dayKey === LATEST_DAY_KEY) {
    return <Navigate to={careerProfilePathFromLatest(pgaId, "")} replace />;
  }

  return (
    <CareerProfileFocusedChrome pgaId={pgaId}>
      {loading ? (
        <CareerGoalsTabSkeleton />
      ) : error ? (
        <div>Error: {error.message}</div>
      ) : !row ? (
        <EmptyState
          title="Snapshot Not Found"
          iconName="search_off"
          caption="No Career Plan changes were recorded for this day."
        />
      ) : (
        <CareerGoalsTab goals={goals} />
      )}
    </CareerProfileFocusedChrome>
  );
}
